import React, { useMemo, useState } from "react";
import { useWizard } from "@/context/WizardContext";
import { ChevronDown, ChevronUp, Eye, CheckCircle2, AlertTriangle, Hash, User, Calendar, Stethoscope, Pill, ClipboardList } from "lucide-react";
import { fmtMDY } from "@/lib/dateFormat";

function Row({ label, value, ok = true, icon: Icon, testid }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      {Icon && <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${ok ? "text-[#0A3D79]" : "text-[#c05746]"}`} />}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#3A5275]">{label}</div>
        <div className={`text-[13.5px] ${ok ? "text-[#0B1E3A]" : "text-[#8a2e1a]"} font-medium truncate`}>{value || <span className="italic text-[#9AADC2]">(empty)</span>}</div>
      </div>
    </div>
  );
}

function Pill2({ ok, label, testid }) {
  return (
    <span
      className={`inline-flex items-center gap-1 h-6 px-2.5 rounded-full text-[11px] font-semibold ${ok ? "bg-[#E7F1FB] text-[#0A3D79] border border-[#B7CADA]" : "bg-[#fbeae5] text-[#8a2e1a] border border-[#e2b6a9]"}`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

/**
 * Pre-submission preview — a collapsible 2-column recap on Step 9 showing
 * everything that will actually print on the PDFs, side-by-side with quick
 * ✓/⚠ badges for the most common RCA reasons a PA gets returned.
 */
export default function PreSubmitPreview({ testid = "presubmit-preview" }) {
  const { data } = useWizard();
  const [open, setOpen] = useState(true);

  const summary = useMemo(() => {
    const c = data.client || {};
    const a = data.agency || {};
    const p = data.physician || {};
    const cms = data.cms485 || {};
    const dx = (data.diagnoses || []).filter((x) => (x.icd10 || x.description || "").trim());
    const meds = (data.medications || []).filter((x) => (x.name || "").trim());
    const medsMissingEnd = meds.filter((m) => !(m.end_date || "").trim()).length;
    const staffing = data.f11136_staffing_grid || {};
    const staffingCellsFilled = Object.values(staffing).reduce((acc, row) => {
      return acc + ["mon","tue","wed","thu","fri","sat","sun"].filter((d) => (row?.[d] || "").trim()).length;
    }, 0);
    const adlScored = Object.values(data.adl || {}).filter((r) => (r?.pcst_level || "").trim()).length;
    const sig = data.signatures || {};
    const sigsDifferent = sig.rn_name && sig.agency_rep_name &&
      sig.rn_name.trim().toLowerCase() !== sig.agency_rep_name.trim().toLowerCase();

    return {
      clientName: `${c.first_name || ""} ${c.middle_initial ? c.middle_initial + " " : ""}${c.last_name || ""}`.trim() || "—",
      memberId: c.forwardhealth_member_id || "",
      dob: fmtMDY(c.date_of_birth),
      assessmentDate: fmtMDY(c.assessment_date),
      agency: a.name || "",
      agencyProvider: a.provider_number || "",
      physician: p.name || "",
      physicianNPI: p.npi || "",
      certFrom: fmtMDY(cms.cert_period_from),
      certTo: fmtMDY(cms.cert_period_to),
      weeklyHours: cms.weekly_hours_requested || "",
      dxCount: dx.length,
      dxPreview: dx.slice(0, 3).map((d) => `${d.icd10 || ""} ${d.description || ""}`.trim()).filter(Boolean),
      medsCount: meds.length,
      medsMissingEnd,
      medsPreview: meds.slice(0, 3).map((m) => `${m.name}${m.dose ? ` ${m.dose}` : ""}${m.frequency ? ` · ${m.frequency}` : ""}`),
      staffingCellsFilled,
      adlScored,
      sigRn: sig.rn_name || "",
      sigRep: sig.agency_rep_name || "",
      sigsDifferent,
      rnDate: fmtMDY(sig.rn_date),
      repDate: fmtMDY(sig.agency_rep_date),
    };
  }, [data]);

  const checks = [
    { ok: !!summary.memberId, label: "Member ID set", testid: `${testid}-check-memberid` },
    { ok: !!summary.physicianNPI, label: "Physician NPI", testid: `${testid}-check-npi` },
    { ok: summary.dxCount > 0, label: `${summary.dxCount} diagnosis`, testid: `${testid}-check-dx` },
    { ok: summary.medsCount > 0 && summary.medsMissingEnd === 0,
      label: summary.medsCount === 0 ? "Add meds" : summary.medsMissingEnd ? `${summary.medsMissingEnd} med${summary.medsMissingEnd===1?"":"s"} missing End Date` : `${summary.medsCount} meds w/ End Date`,
      testid: `${testid}-check-meds` },
    { ok: summary.adlScored === 6, label: `${summary.adlScored}/6 ADLs scored`, testid: `${testid}-check-adl` },
    { ok: !!summary.certFrom && !!summary.certTo, label: "Cert period set", testid: `${testid}-check-cert` },
    { ok: summary.sigsDifferent, label: summary.sigsDifferent ? "Signers distinct" : "RN ≠ Agency Rep required", testid: `${testid}-check-sigs` },
  ];

  return (
    <section className="card-surface fade-up" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#0A3D79] text-[#F7FAFC] flex items-center justify-center">
            <Eye className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275]">Final review</div>
            <h3 className="font-display text-xl font-semibold text-[#0B1E3A]">Pre-submission preview</h3>
            <p className="text-[13px] text-[#3A5275]">Exactly what will print on the 5 PDFs. Edit anything that looks wrong before hitting Generate.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="btn-ghost"
          data-testid={`${testid}-toggle`}
        >
          {open ? <>Hide preview <ChevronUp className="w-4 h-4" /></> : <>Show preview <ChevronDown className="w-4 h-4" /></>}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {checks.map((c) => <Pill2 key={c.label} ok={c.ok} label={c.label} testid={c.testid} />)}
      </div>

      {open && (
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-xl border border-[#D6E3EF] bg-[#F0F6FC] p-4">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-1">Identity (F-11133 / F-11136 / CMS-485)</div>
            <Row icon={User}       label="Client"           value={summary.clientName}     testid={`${testid}-row-client`} />
            <Row icon={Hash}       label="Member ID"        value={summary.memberId}       ok={!!summary.memberId} testid={`${testid}-row-memberid`} />
            <Row icon={Calendar}   label="Date of Birth"    value={summary.dob}            testid={`${testid}-row-dob`} />
            <Row icon={Calendar}   label="Assessment Date"  value={summary.assessmentDate} testid={`${testid}-row-assessmentdate`} />
            <div className="h-px bg-[#D6E3EF] my-2" />
            <Row icon={Stethoscope} label="Agency"          value={summary.agency}          testid={`${testid}-row-agency`} />
            <Row icon={Hash}        label="Provider #"      value={summary.agencyProvider}  testid={`${testid}-row-providernum`} />
            <div className="h-px bg-[#D6E3EF] my-2" />
            <Row icon={Stethoscope} label="Physician"       value={summary.physician}       testid={`${testid}-row-physician`} />
            <Row icon={Hash}        label="Physician NPI"   value={summary.physicianNPI}    ok={!!summary.physicianNPI} testid={`${testid}-row-npi`} />
          </div>

          <div className="rounded-xl border border-[#D6E3EF] bg-[#F0F6FC] p-4">
            <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#3A5275] mb-1">Clinical (F-11136 / CMS-485)</div>
            <Row icon={Calendar} label="Cert Period" value={(summary.certFrom || summary.certTo) ? `${summary.certFrom || "—"} → ${summary.certTo || "—"}` : ""} ok={!!summary.certFrom && !!summary.certTo} testid={`${testid}-row-cert`} />
            <Row icon={ClipboardList} label="Weekly Hours Requested" value={summary.weeklyHours} testid={`${testid}-row-hours`} />
            <Row icon={ClipboardList} label="Staffing grid cells" value={`${summary.staffingCellsFilled} filled`} testid={`${testid}-row-staffing`} />
            <Row icon={ClipboardList} label="ADLs scored" value={`${summary.adlScored} / 6`} ok={summary.adlScored === 6} testid={`${testid}-row-adl`} />
            <div className="h-px bg-[#D6E3EF] my-2" />
            <Row icon={ClipboardList} label={`Diagnoses · ${summary.dxCount}`} value={summary.dxPreview.join(" · ") || "—"} ok={summary.dxCount > 0} testid={`${testid}-row-diagnoses`} />
            <Row icon={Pill}          label={`Medications · ${summary.medsCount}${summary.medsMissingEnd ? ` (⚠ ${summary.medsMissingEnd} missing End Date)` : ""}`}
              value={summary.medsPreview.join(" · ") || "—"}
              ok={summary.medsCount > 0 && summary.medsMissingEnd === 0}
              testid={`${testid}-row-medications`} />
            <div className="h-px bg-[#D6E3EF] my-2" />
            <Row icon={User} label="RN Signature" value={summary.sigRn ? `${summary.sigRn} · ${summary.rnDate || "date?"}` : ""} ok={!!summary.sigRn && !!summary.rnDate} testid={`${testid}-row-rnsig`} />
            <Row icon={User} label="Agency Rep Signature" value={summary.sigRep ? `${summary.sigRep} · ${summary.repDate || "date?"}` : ""} ok={!!summary.sigRep && !!summary.repDate && summary.sigsDifferent} testid={`${testid}-row-repsig`} />
          </div>
        </div>
      )}
    </section>
  );
}
